import { useMemo, useRef, useEffect, useState } from 'react';
import { BarChart } from '@mui/x-charts/BarChart';
import Card from '@mui/material/Card';
import { config } from 'Constants';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Site color palette
const CHART_COLORS = {
    primary: '#e91e63',
    secondary: '#7b809a',
    info: '#1A73E8',
    success: '#4CAF50',
    warning: '#fb8c00',
    error: '#f44335',
    light: '#f0f2f5',
    dark: '#344767',
    grid: 'rgba(123, 128, 154, 0.2)', // Secondary color with opacity for grid
    text: '#344767', // Dark color for text
};

function UsersSummary({ projects = [] }) {
    const labels = [];
    let counterWin = {};
    const valuesWin = [];
    let counterLost = {};
    const valuesLost = [];
    let counterUnknown = {};
    const valuesUnknown = [];

    // Kullanıcı bazlı proje durumlarını hesapla
    projects.forEach((project) => {
        // for user's project
        project.responsibles?.forEach((responsible) => {
            if (responsible.role !== 'superadmin') {
                if (isNaN(counterWin[responsible.username])) counterWin[responsible.username] = 0;
                if (isNaN(counterLost[responsible.username])) counterLost[responsible.username] = 0;
                if (isNaN(counterUnknown[responsible.username])) counterUnknown[responsible.username] = 0;

                if (project.status) {
                    if (
                        project.status.tag._id === config.statusID.uretiliyor ||
                        project.status.tag._id === config.statusID.uretildi ||
                        project.status.tag._id === config.statusID.kazanildi
                    )
                        counterWin[responsible.username] += 1;

                    if (project.status.tag._id === config.statusID.kaybedildi) counterLost[responsible.username] += 1;
                    if (project.status.tag._id === config.statusID.teklifGonderildi)
                        counterUnknown[responsible.username] += 1;
                } else {
                    counterUnknown[responsible.username] += 1;
                }
            }
        });
    });

    Object.keys(counterWin).forEach((key) => {
        labels.push(key);
        valuesWin.push(counterWin[key]);
        valuesLost.push(counterLost[key]);
        valuesUnknown.push(counterUnknown[key]);
    });

    const hasData = labels.length > 0;

    // Responsive chart width için
    const chartContainerRef = useRef(null);
    const [chartWidth, setChartWidth] = useState(0);

    useEffect(() => {
        const updateChartWidth = () => {
            if (chartContainerRef.current) {
                setChartWidth(chartContainerRef.current.offsetWidth);
            }
        };

        // İlk render'da genişliği ayarla
        updateChartWidth();

        // ResizeObserver ile container boyutu değiştiğinde genişliği güncelle
        const resizeObserver = new ResizeObserver(updateChartWidth);
        if (chartContainerRef.current) {
            resizeObserver.observe(chartContainerRef.current);
        }

        // Cleanup
        return () => {
            if (chartContainerRef.current) {
                resizeObserver.unobserve(chartContainerRef.current);
            }
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <Card sx={{ height: '100%' }}>
            {useMemo(
                () => (
                    <MDBox
                        ref={chartContainerRef}
                        variant="gradient"
                        bgColor="white"
                        borderRadius="xl"
                        coloredShadow="white"
                        height="12.5rem"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {chartWidth > 0 && hasData ? (
                            <BarChart
                                width={chartWidth}
                                height={220}
                                series={[
                                    {
                                        data: valuesWin,
                                        label: 'Kazanılan',
                                        stack: 'total',
                                        color: CHART_COLORS.success,
                                        valueFormatter: (value) => `Kazanılan: ${value}`,
                                    },
                                    {
                                        data: valuesLost,
                                        label: 'Kaybedilen',
                                        stack: 'total',
                                        color: CHART_COLORS.primary,
                                        valueFormatter: (value) => `Kaybedilen: ${value}`,
                                    },
                                    {
                                        data: valuesUnknown,
                                        label: 'Bilinmeyen',
                                        stack: 'total',
                                        color: CHART_COLORS.light,
                                        valueFormatter: (value) => `Bilinmeyen: ${value}`,
                                    },
                                ]}
                                xAxis={[
                                    {
                                        data: labels,
                                        scaleType: 'band',
                                        tickLabelStyle: {
                                            color: CHART_COLORS.text,
                                            fontSize: 12,
                                            fontWeight: 600,
                                            fontFamily: 'Roboto',
                                        },
                                        gridStyle: {
                                            display: false,
                                        },
                                    },
                                ]}
                                yAxis={[
                                    {
                                        tickLabelStyle: {
                                            color: CHART_COLORS.text,
                                            fontSize: 12,
                                            fontFamily: 'Roboto',
                                        },
                                        gridStyle: {
                                            strokeDasharray: '5 5',
                                            stroke: CHART_COLORS.grid,
                                        },
                                    },
                                ]}
                                tooltip={{
                                    trigger: 'item',
                                    formatter: (params) => `${params.seriesName}: ${params.value}`,
                                }}
                                slotProps={{
                                    legend: {
                                        hidden: false,
                                        direction: 'row',
                                        position: {
                                            vertical: 'top',
                                            horizontal: 'right',
                                        },
                                        labelStyle: {
                                            fontSize: 12,
                                            color: CHART_COLORS.text,
                                            fontWeight: 600,
                                        },
                                    },
                                }}
                                margin={{ left: 5, right: 10, top: 10, bottom: 30 }}
                                axisHighlight={{
                                    x: 'line',
                                    y: 'line',
                                }}
                                sx={{
                                    '.MuiBarElement-root': {
                                        borderRadius: 2, // Yuvarlatılmış bar köşeleri
                                        ':hover': {
                                            filter: 'brightness(1.1)',
                                        },
                                    },
                                    '.MuiChartsTooltip-table': {
                                        backgroundColor: CHART_COLORS.dark,
                                        color: '#fff',
                                        borderRadius: '8px',
                                        padding: '12px',
                                        fontSize: '12px',
                                        boxShadow: '0 4px 12px rgba(52, 71, 103, 0.3)',
                                    },
                                }}
                            />
                        ) : (
                            <MDTypography color="text" variant="button">
                                Gösterilecek kullanıcı verisi bulunamadı
                            </MDTypography>
                        )}
                    </MDBox>
                ),
                [labels, valuesWin, valuesLost, valuesUnknown, chartWidth, hasData]
            )}
        </Card>
    );
}

export default UsersSummary;
